import { useEffect, useState, useContext } from 'react';
import {
  calculateFairShare,
  percentLess,
  toPrecision,
  toNonDivisibleNumber,
  toReadableNumber,
  percent,
  ONLY_ZEROS,
} from '../utils/numbers';
import { getStakedListByAccountId } from '../services/farm';
import {
  DEFAULT_PAGE_LIMIT,
  getAllWatchListFromDb,
  getPoolDetails,
  getSharesInPool,
  getTotalPools,
  parsePool,
  parsePoolNew,
  Pool,
  PoolDetails,
  removeLiquidityFromPool,
  getPoolsFromCache,
} from '../services/pool';
import db, { WatchList } from '../store/RefDatabase';

import { useTokenPriceList } from './token';
import _, { trim } from 'lodash';
import {
  getPoolMonthVolume,
  getPoolMonthTVL,
  getPoolsByIds,
  get24hVolume,
  _order,
  _search,
  getTopPools,
  getSearchResult,
  get24hVolumes,
  getV3PoolVolumeById,
  getAllV3Pool24Volume,
  getV3poolTvlById,
  getClassicPoolSwapRecentTransaction,
  ClassicPoolSwapTransaction,
  ClassicPoolLiquidityRecentTransaction,
  getClassicPoolLiquidityRecentTransaction,
  DCLPoolSwapTransaction,
  DCLPoolLiquidityRecentTransaction,
  getDCLPoolSwapRecentTransaction,
  getDCLPoolLiquidityRecentTransaction,
  getLimitOrderRecentTransaction,
  LimitOrderRecentTransaction,
  getDCLTopBinFee,
  getIndexerStatus,
} from '../services/indexer';
import {
  ftGetTokenMetadata,
  ftGetTokensMetadata,
} from '../services/ft-contract';
import BigNumber from 'bignumber.js';
import { ALL_STABLE_POOL_IDS, isStablePool } from '../services/near';
import { WalletContext } from '../utils/wallets-integration';
import getConfig from '../services/config';
import { useFarmStake, useShadowRecord } from './farm';
import Big from 'big.js';
import { PoolInfo, get_pool } from '../services/swapV3';
import { getStablePoolDecimal } from 'src/utils/stablePoolFormatting';
import {
  get_default_config_for_chart,
  get_custom_config_for_chart,
} from '../components/d3Chart/config';
import {
  IChartItemConfig,
  IChartConfig,
} from '../components/d3Chart/interfaces';
import { formatPercentage } from '../components/d3Chart/utils';
import { useFarmerSeedsStore } from 'src/stores/liquidityStores';
import { get_account, ILock } from '../services/lp-locker';
import { getPointByPrice, getPriceByPoint } from '../services/dcl/dclMath';
import { get_seed, list_seed_farms } from '../services/farmBoostViews';

export const useBatchTotalShares = (
  ids: (string | number)[],
  finalStakeList: Record<string, string>,
  stakeListDone: boolean
) => {
  const { globalState } = useContext(WalletContext);
  const isSignedIn = globalState.isSignedIn;
  const [batchShares, setBatchShares] = useState<string[]>();
  const [batchFarmStake, setBatchFarmStake] = useState<(string | number)[]>();
  const [batchLpLocked, setBatchLpLocked] = useState<(string | number)[]>();
  const [sharesDone, setSharesDone] = useState<boolean>(false);
  const [accountLocked, setAccountLocked] = useState<Record<string, ILock>>();

  const getFarmStake = (pool_id: number) => {
    const farmStake = '0';

    const seedIdList: string[] = Object.keys(finalStakeList);
    let tempFarmStake: string | number = '0';
    seedIdList.forEach((seed) => {
      const id = Number(seed.split('@')[1]);
      if (id == pool_id) {
        tempFarmStake = BigNumber.sum(
          farmStake,
          finalStakeList[seed]
        ).valueOf();
      }
    });

    return tempFarmStake;
  };
  useEffect(() => {
    if (isSignedIn) {
      get_account().then((locked) => {
        setAccountLocked(locked?.locked_tokens || {});
      });
    }
  }, [isSignedIn]);
  useEffect(() => {
    if (
      !ids ||
      !finalStakeList ||
      !isSignedIn ||
      !stakeListDone ||
      !accountLocked
    )
      return undefined;
    getShares();
  }, [
    ids?.join('-'),
    finalStakeList,
    isSignedIn,
    stakeListDone,
    accountLocked,
  ]);
  async function getShares() {
    try {
      const shareInPoolsSettled = await Promise.allSettled(
        ids.map((id) => getSharesInPool(Number(id)))
      );
      const shareInPools = shareInPoolsSettled
        .filter((settledResult) => settledResult.status === 'fulfilled')
        .map((fulfilledResult: any) => fulfilledResult.value);
      const shareInFarms = ids.map((id) => getFarmStake(Number(id)));
      const shareInLocked = ids.map((id: string) => {
        const key = `${getConfig().REF_FI_CONTRACT_ID}@:${id}`;
        return accountLocked?.[key]?.locked_balance || '0';
      });
      setBatchShares(shareInPools);
      setBatchFarmStake(shareInFarms);
      setBatchLpLocked(shareInLocked);
      setSharesDone(true);
    } catch (error) {
      console.error('getSharesErr', ids, error);
    }
  }
  return {
    sharesDone,
    shares: batchShares,
    batchTotalShares:
      ids?.map((id, index) => {
        return new Big(batchShares?.[index] || '0')
          .plus(new Big(batchFarmStake?.[index] || '0'))
          .plus(new Big(batchLpLocked?.[index] || '0'))
          .toNumber();
      }) || undefined,
  };
};

export const useStakeListByAccountId = () => {
  const { globalState } = useContext(WalletContext);

  const isSignedIn = globalState.isSignedIn;

  const [stakeList, setStakeList] = useState<Record<string, string>>({});
  const [v2StakeList, setV2StakeList] = useState<Record<string, string>>({});

  const [finalStakeList, setFinalStakeList] = useState<Record<string, string>>(
    {}
  );
  const [stakeListDone, setStakeListDone] = useState<boolean>(false);

  useEffect(() => {
    if (!isSignedIn) return;
    getStakedListByAccountId({})
      .then(({ stakedList, finalStakeList, v2StakedList }) => {
        setStakeList(stakedList);
        setV2StakeList(v2StakedList);
        setFinalStakeList(finalStakeList);
        setStakeListDone(true);
      })
      .catch(() => {});
  }, [isSignedIn]);

  return {
    stakeList,
    v2StakeList,
    finalStakeList,
    stakeListDone,
  };
};

export const usePool = (id: number | string) => {
  const { globalState } = useContext(WalletContext);

  const isSignedIn = globalState.isSignedIn;

  const [pool, setPool] = useState<PoolDetails>();
  const [shares, setShares] = useState<string>('0');
  const [stakeList, setStakeList] = useState<Record<string, string>>({});
  const [v2StakeList, setV2StakeList] = useState<Record<string, string>>({});
  const [finalStakeList, setFinalStakeList] = useState<Record<string, string>>(
    {}
  );

  useEffect(() => {
    getPoolDetails(Number(id)).then(setPool);
    getSharesInPool(Number(id))
      .then(setShares)
      .catch(() => setShares);

    getStakedListByAccountId({})
      .then(({ stakedList, finalStakeList, v2StakedList }) => {
        setStakeList(stakedList);
        setV2StakeList(v2StakedList);
        setFinalStakeList(finalStakeList);
      })
      .catch(() => {});
  }, [id, isSignedIn]);

  return {
    pool,
    shares,
    stakeList,
    v2StakeList,
    finalStakeList,
  };
};

interface LoadPoolsOpts {
  accumulate?: boolean;
  tokenName?: string;
  sortBy?: string;
  order?: string;
  getTopPoolsProps?: any;
}

export const usePools = (props: {
  searchTrigger?: Boolean;
  tokenName?: string;
  sortBy?: string;
  order?: string;
  getTopPoolsProps?: any;
  activeTab?: any;
}) => {
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [pools, setPools] = useState<Pool[]>([]);
  const [rawPools, setRawPools] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [cardLoading, setCardLoading] = useState(true);
  const [requestPoolList, setRequestPoolList] = useState<string[]>();

  const [shortSavePools, setShortSavePools] = useState([]);
  useEffect(() => {
    setPools([]);
    if (props.activeTab == 'v1') setLoading(true);
  }, [props.activeTab]);

  useEffect(() => {
    if (!loading && rawPools.length) {
      setRequestPoolList(
        rawPools.map((pool) => pool.id.toString()).concat(ALL_STABLE_POOL_IDS)
      );
    }
  }, [loading, rawPools.length]);

  const volumes = useDayVolumesPools(requestPoolList);

  const nextPage = () => setPage((page) => page + 1);

  function _loadPools({ getTopPoolsProps }: LoadPoolsOpts) {
    getSearchResult({ ...getTopPoolsProps, token_list: props.tokenName })
      .then(async (searchResults) => {
        const pools =
          searchResults.length > 0
            ? searchResults.map((rawPool) => parsePoolNew(rawPool))
            : [];

        setShortSavePools(pools);

        // setHasMore(pools.length === DEFAULT_PAGE_LIMIT);

        setPools(pools);
      })
      .finally(() => {
        setLoading(false);
        setCardLoading(false);
      });
  }

  function _loadPoolsOri({
    accumulate = true,
    tokenName,
    sortBy,
    order,
  }: LoadPoolsOpts) {
    getTopPools()
      .then(async (rawPools) => {
        const pools =
          rawPools.length > 0
            ? rawPools.map((rawPool) => parsePool(rawPool))
            : await getPoolsFromCache({
                page,
                tokenName,
                column: sortBy,
                order,
              });

        setRawPools(rawPools);

        setHasMore(pools.length === DEFAULT_PAGE_LIMIT);

        setPools((currentPools) =>
          pools.reduce<Pool[]>(
            (uniquePools: Pool[], pool) => {
              if (
                uniquePools.some(
                  (existingPool) =>
                    existingPool.fee === pool.fee &&
                    existingPool.tokenIds.includes(pool.tokenIds[0]) &&
                    existingPool.tokenIds.includes(pool.tokenIds[1]) &&
                    existingPool.shareSupply === pool.shareSupply
                )
              )
                return uniquePools;
              uniquePools.push(pool);
              return uniquePools;
            },
            accumulate ? currentPools.slice() : []
          )
        );
      })
      .finally(() => {
        setLoading(false);
        setCardLoading(false);
      });
  }
  const loadPools = props.activeTab == 'v1' ? _loadPools : _loadPoolsOri;
  useEffect(() => {
    if (props.activeTab != 'v1') {
      const args = {
        page,
        perPage: DEFAULT_PAGE_LIMIT,
        tokenName: trim(props.tokenName),
        column: props.sortBy,
        order: props.order,
      };

      const newPools = _order(args, _search(args, rawPools)).map((rawPool) =>
        parsePool(rawPool)
      );
      setPools(newPools);
    }
  }, [props.sortBy, props.order, props.tokenName, rawPools]);

  useEffect(() => {
    if (props.activeTab == 'v1') {
      if (props.tokenName) {
        setCardLoading(true);
        getSearchResult({
          ...props.getTopPoolsProps,
          token_list: props.tokenName,
        })
          .then(async (searchResults) => {
            const pools =
              searchResults.length > 0
                ? searchResults.map((rawPool) => parsePoolNew(rawPool))
                : [];
            setShortSavePools(pools);
            setHasMore(pools.length === DEFAULT_PAGE_LIMIT);
            setPools(pools);
          })
          .finally(() => {
            setLoading(false);
            setCardLoading(false);
          });
      } else {
        setCardLoading(true);
        loadPools({
          getTopPoolsProps: props.getTopPoolsProps,
        });
      }
    }
  }, [props.tokenName]);

  useEffect(() => {
    if (props.activeTab != 'v1') {
      setLoading(true);
      loadPools({
        accumulate: true,
        tokenName: props.tokenName,
        sortBy: props.sortBy,
        order: props.order,
      });
    }
  }, [page, props.activeTab]);

  useEffect(() => {
    if (props.activeTab == 'v1') {
      setCardLoading(true);
      loadPools({
        getTopPoolsProps: props.getTopPoolsProps,
      });
    }
  }, [
    props.getTopPoolsProps.farm,
    props.getTopPoolsProps.hide_low_pool,
    props.getTopPoolsProps.type,
    props.getTopPoolsProps.sort,
    props.getTopPoolsProps.order,
    props.getTopPoolsProps.order_by,
    props.getTopPoolsProps.offset,
    props.getTopPoolsProps.token_type,
  ]);
  return {
    pools,
    hasMore,
    nextPage,
    loading,
    volumes,
    cardLoading,
  };
};

export const useWatchPools = () => {
  const [watchList, setWatchList] = useState<WatchList[]>([]);

  const [watchPools, setWatchPools] = useState<Pool[]>([]);
  const [watchV2Pools, setWatchV2Pools] = useState<PoolInfo[]>([]);
  const [watchV2PoolsFinal, setWatchV2PoolsFinal] = useState<PoolInfo[]>([]);
  const tokenPriceList = useTokenPriceList();
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      getAllWatchListFromDb({})
        .then((watchlist) => {
          if (!cancelled) {
            setWatchList(_.orderBy(watchlist || [], 'update_time', 'desc'));
          }
        })
        .catch(() => {
          if (!cancelled) setWatchList([]);
        });
    };
    load();
    window.addEventListener('watchlist-changed', load);
    return () => {
      cancelled = true;
      window.removeEventListener('watchlist-changed', load);
    };
  }, []);
  useEffect(() => {
    if (watchList.length == 0) {
      setWatchPools([]);
      setWatchV2Pools([]);
      setWatchV2PoolsFinal([]);
      return;
    }
    const ids = watchList.map((watchedPool) => watchedPool.pool_id);
    if (ids.length === 0) return;
    const ids_v1: string[] = [];
    const ids_v2: string[] = [];
    ids.forEach((id) => {
      if (id.split('|').length == 3) {
        ids_v2.push(id);
      } else {
        ids_v1.push(id);
      }
    });
    if (ids_v1.length > 0) {
      //
      const knownPoolIds = new Set(ALL_STABLE_POOL_IDS);
      const knownIds_v1 = ids_v1.filter((id) => knownPoolIds.has(id));
      const unknownIds_v1 = ids_v1.filter((id) => !knownPoolIds.has(id));

      Promise.all([
        getPoolsByIds({ pool_ids: knownIds_v1 })
          .then((rawPools) => {
            const resPools = rawPools.map((pool) => parsePool(pool));

            return resPools;
          })
          .then((resPools) => {
            return Promise.all(
              resPools.map(async (pool) => {
                return {
                  ...pool,
                  metas: await ftGetTokensMetadata(pool.tokenIds),
                };
              })
            );
          }),
        getSearchResult({
          onlyUseId: true,
          pool_id_list: unknownIds_v1.join(','),
        })
          .then((rawPools) => {
            const resPools = rawPools.map((pool) => parsePoolNew(pool));

            return resPools;
          })
          .then((resPools) => {
            return Promise.all(
              resPools.map(async (pool) => {
                return {
                  ...pool,
                  metas: await ftGetTokensMetadata(pool.tokenIds),
                };
              })
            );
          }),
      ]).then(([classicPools, stablePools]) => {
        const allPools = [...classicPools, ...stablePools];
        setWatchPools(allPools);
      });
    } else {
      setWatchPools([]);
    }

    //
    if (ids_v2.length > 0) {
      getV2PoolsByIds(ids_v2).then((v2Pools: PoolInfo[]) => {
        setWatchV2Pools(
          v2Pools.filter((pool) => {
            if (pool) return true;
          })
        );
      });
    }
  }, [watchList]);
  useEffect(() => {
    if (watchV2Pools.length > 0 && Object.keys(tokenPriceList).length > 1) {
      getV2Poolsfinal();
    }
  }, [watchV2Pools, Object.keys(tokenPriceList).length]);

  async function getV2PoolsByIds(ids: string[]): Promise<PoolInfo[]> {
    const poolDetailPromise = ids.map((id) => {
      return get_pool(id);
    });
    const poolList_init = await Promise.all(poolDetailPromise);
    const poolList = poolList_init.filter((pool: PoolInfo) => {
      if (pool) return true;
    });
    const poolListPromise = poolList.map(async (pool: PoolInfo) => {
      const { token_x, token_y } = pool;
      const token_x_meta = await ftGetTokenMetadata(token_x);
      const token_y_meta = await ftGetTokenMetadata(token_y);
      pool.token_x_metadata = token_x_meta;
      pool.token_y_metadata = token_y_meta;
      return pool;
    });
    const poolListDealt = await Promise.all(poolListPromise);
    return poolListDealt;
  }
  function getV2Poolsfinal() {
    watchV2Pools.forEach((pool: PoolInfo) => {
      const { token_x, token_y } = pool;
      const pricex = tokenPriceList[token_x]?.price || 0;
      const pricey = tokenPriceList[token_y]?.price || 0;
      const { total_x, total_y, total_fee_x_charged, total_fee_y_charged } =
        pool;
      const totalX = new BigNumber(total_x)
        .minus(total_fee_x_charged)
        .toFixed();
      const totalY = new BigNumber(total_y)
        .minus(total_fee_y_charged)
        .toFixed();

      const tvlx =
        Number(toReadableNumber(pool.token_x_metadata.decimals, totalX)) *
        Number(pricex);
      const tvly =
        Number(toReadableNumber(pool.token_y_metadata.decimals, totalY)) *
        Number(pricey);
      pool.tvl = tvlx + tvly;
    });
    setWatchV2PoolsFinal(watchV2Pools);
  }
  return { watchPools, watchV2PoolsFinal, watchList };
};

export const useAllPools = () => {
  const [allPools, setAllPools] = useState<number>();

  useEffect(() => {
    getTotalPools().then((totalPools) => {
      setAllPools(totalPools);
    });
  }, []);

  return allPools;
};

export const useRemoveLiquidity = ({
  pool,
  shares,
  slippageTolerance,
}: {
  pool: Pool;
  shares: string;
  slippageTolerance: number;
}) => {
  const minimumAmounts = Object.entries(pool.supplies).reduce<{
    [tokenId: string]: string;
  }>((amountsByTokenId, [tokenId, totalSupply]) => {
    amountsByTokenId[tokenId] = toPrecision(
      percentLess(
        slippageTolerance,
        calculateFairShare({
          shareOf: totalSupply,
          contribution: shares,
          totalContribution: pool.shareSupply,
        })
      ),
      0
    );
    return amountsByTokenId;
  }, {});

  const removeLiquidity = () => {
    return removeLiquidityFromPool({
      id: pool.id,
      shares,
      minimumAmounts,
    });
  };

  return {
    removeLiquidity,
    minimumAmounts,
  };
};

export interface volumeType {
  pool_id: string;
  date_string: string;
  fiat_volume: string;
  asset_volume: string;
  volume_dollar: string;
}

export interface volumeDataType {
  pool_id: string;
  dateString: string;
  fiat_volume: string;
  asset_volume: string;
  volume_dollar: number;
}

export const useMonthVolume = (pool_id: string) => {
  const [monthVolumeById, setMonthVolumeById] = useState<volumeDataType[]>();
  useEffect(() => {
    getPoolMonthVolume(pool_id).then((monthVolumes) => {
      const monthVolume = monthVolumes
        .map((dailyVolume, index) => {
          return {
            ...dailyVolume,
            volume_dollar: Number(dailyVolume.volume_dollar),
            dateString: dailyVolume.date_string,
          };
        })
        .reverse();
      setMonthVolumeById(monthVolume);
    });
  }, []);

  return monthVolumeById;
};

export interface TVLType {
  // pool_id: string;
  // asset_amount: string;
  // fiat_amount: string;
  // asset_price: string;
  // fiat_price: string;
  // asset_tvl: string;
  // fiat_tvl: string;
  // date: string;
  date_string: string;
  pool_id: string;
  time: number;
  tvl: number;
}
export interface TVLDataType {
  // pool_id: string;
  // asset_amount: string;
  // fiat_amount: string;
  // asset_price: string;
  // fiat_price: string;
  // asset_tvl: number;
  // fiat_tvl: number;
  date: string;
  total_tvl: number;
  // scaled_tvl: number;
  pool_id: string;
  time: number;
  tvl: number;
}

export const useMonthTVL = (pool_id: string) => {
  const [monthTVLById, setMonthTVLById] = useState<TVLDataType[]>();
  useEffect(() => {
    getPoolMonthTVL(pool_id).then((monthTvls) => {
      // const minDay = _.minBy(res, (o) => {
      //   return Number(o.asset_tvl) + Number(o.fiat_tvl);
      // });
      // const minValue = Number(minDay?.asset_tvl) + Number(minDay?.fiat_tvl);

      const monthTVL = monthTvls
        .map((dailyTvl, index) => {
          return {
            ...dailyTvl,
            total_tvl: Number(dailyTvl.tvl),
            date: dailyTvl.date_string,
            // asset_tvl: Number(v?.asset_tvl),
            // fiat_tvl: Number(v?.fiat_tvl),
            // total_tvl: Number(v?.fiat_tvl) + Number(v?.asset_tvl),
            // scaled_tvl:
            //   Number(v?.fiat_tvl) + Number(v?.asset_tvl) - minValue * 0.99,
          };
        })
        .reverse();
      setMonthTVLById(monthTVL);
    });
  }, []);

  return monthTVLById;
};

export const useDayVolumesPools = (pool_ids: (string | number)[]) => {
  const [volumes, setVolumes] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!pool_ids || pool_ids?.length === 0) return;

    get24hVolumes(pool_ids).then((fetchedVolumes) => {
      const volumes = fetchedVolumes.reduce(
        (volumesByPoolId, volume, index) => {
          return { ...volumesByPoolId, [pool_ids[index]]: volume };
        },
        {}
      );

      setVolumes(volumes);
    });
  }, [pool_ids?.join('-')]);

  return volumes;
};

export const useDayVolume = (pool_id: string) => {
  const [dayVolume, setDayVolume] = useState<string>();
  useEffect(() => {
    get24hVolume(pool_id).then(setDayVolume);
  }, [pool_id]);
  return dayVolume;
};

export const useSeedDetail = (pool_id: string | number) => {
  const seed_id = getConfig().REF_FI_CONTRACT_ID + '@' + pool_id.toString();

  const [seedDetail, setSeedDetail] = useState<any>();

  useEffect(() => {
    get_seed(seed_id).then((fetchedSeedDetail) => {
      setSeedDetail(fetchedSeedDetail);
    });
  }, []);

  return seedDetail;
};

export const useSeedFarms = (pool_id: string | number) => {
  const [seedFarms, setSeedFarms] = useState<any>();

  const seed_id = getConfig().REF_FI_CONTRACT_ID + '@' + pool_id.toString();

  useEffect(() => {
    list_seed_farms(seed_id)
      .then(async (fetchedSeedFarms) => {
        if (!fetchedSeedFarms) return null;

        const parsedRes = fetchedSeedFarms.filter(
          (farm: any) => farm.status !== 'Ended'
        );

        if (!parsedRes || parsedRes.length === 0) {
          return;
        }

        return Promise.all(
          parsedRes
            // .filter((f: any) => noRunning || f.status === 'Running')
            .filter((farm: any) => farm.status != 'Ended')
            .map(async (farm: any) => {
              const token_meta_data = await ftGetTokenMetadata(
                farm.terms.reward_token
              );

              const daily_reward = farm.terms.daily_reward;

              const readableNumber = toReadableNumber(
                token_meta_data.decimals,
                daily_reward
              );

              const yearReward = Number(readableNumber) * 365;

              return {
                ...farm,
                token_meta_data,
                yearReward,
              };
            })
        );
      })
      .then(setSeedFarms);
  }, [pool_id]);

  return seedFarms;
};

export const useSeedFarmsByPools = (_pools: Pool[]) => {
  return {
    farmAprById: {} as Record<string, number>,
    loadingSeedsDone: true,
  };
};

export const useYourliquidity = (poolId: number) => {
  const { pool, shares, stakeList, v2StakeList, finalStakeList } =
    usePool(poolId);

  // todo: check if can remove useFarmStake, use farmerSeeds only
  const farmStakeV1 = useFarmStake({ poolId, stakeList });
  const farmStakeV2Ori = useFarmStake({ poolId, stakeList: v2StakeList });
  const farmerSeeds = useFarmerSeedsStore((state) => state.farmerSeeds);
  const poolSeed = farmerSeeds[poolId];
  const farmStakeV2 = poolSeed
    ? new BigNumber(poolSeed.free_amount).plus(poolSeed.shadow_amount).toFixed()
    : farmStakeV2Ori || '0';

  const farmStakeTotal = useFarmStake({ poolId, stakeList: finalStakeList });
  const { poolShadowRecord } = useShadowRecord(poolId);
  const { shadow_in_burrow } = poolShadowRecord || {};
  const userTotalShare = BigNumber.sum(shares, farmStakeTotal);
  const userTotalShareToString = userTotalShare
    .toNumber()
    .toLocaleString('fullwide', { useGrouping: false });

  const processShare = (share, stakeAmount = '0') => {
    const totalShare = share
      ? BigNumber.sum(share, farmStakeTotal)
      : BigNumber('0');
    const totalShareString = totalShare
      .toNumber()
      .toLocaleString('fullwide', { useGrouping: false });
    const sharePercent = totalShare.isGreaterThan(0)
      ? percent(
          stakeAmount,
          totalShare
            .toNumber()
            .toLocaleString('fullwide', { useGrouping: false })
        ).toString()
      : '0';

    return { totalShare, totalShareString, sharePercent, stakeAmount };
  };
  const shadowBurrowShare = processShare(shares, shadow_in_burrow);

  return {
    pool,
    shares,
    stakeList,
    v2StakeList,
    finalStakeList,
    farmStakeTotal,
    farmStakeV1,
    farmStakeV2,
    userTotalShare,
    userTotalShareToString,
    shadowBurrowShare,
  };
};

export const usePoolShare = (id: string | number, decimalLimit?: number) => {
  const [myPoolShare, setMyPoolShare] = useState<string>('0');
  const { globalState } = useContext(WalletContext);
  const isSignedIn = globalState.isSignedIn;

  useEffect(() => {
    if (!isSignedIn) return;
    getSharesInPool(Number(id)).then((shares) => {
      setMyPoolShare(toReadableNumber(24, shares));
    });
  }, [isSignedIn]);

  if (!decimalLimit) {
    return myPoolShare;
  } else {
    return ONLY_ZEROS.test(toNonDivisibleNumber(decimalLimit, myPoolShare))
      ? '0'
      : myPoolShare;
  }
};

export const useV3VolumeChart = (pool_id: string) => {
  const [volumes, setVolumes] = useState<any[]>();
  useEffect(() => {
    getV3PoolVolumeById(pool_id)
      .then((dailyVolumes) => {
        dailyVolumes.forEach((dailyVolume) => {
          dailyVolume.volume_dollar = +dailyVolume.volume;
        });
        dailyVolumes.sort((volumeA, volumeB) => {
          const timeDifference =
            new Date(volumeA.dateString).getTime() -
            new Date(volumeB.dateString).getTime();
          return timeDifference;
        });
        setVolumes(dailyVolumes);
      })
      .catch(() => {
        setVolumes([]);
      });
  }, []);
  return volumes;
};
export const useV3TvlChart = (pool_id: string) => {
  const [tvls, setTvls] = useState<any[]>();
  useEffect(() => {
    getV3poolTvlById(pool_id)
      .then((dailyTvls) => {
        dailyTvls.forEach((dailyTvl) => {
          (dailyTvl.total_tvl = +dailyTvl.tvl || 0),
            (dailyTvl.scaled_tvl = +dailyTvl.tvl || 0),
            (dailyTvl.date = dailyTvl.dateString);
        });
        dailyTvls.sort((tvlA, tvlB) => {
          return new Date(tvlA.date).getTime() - new Date(tvlB.date).getTime();
        });
        setTvls(dailyTvls);
      })
      .catch(() => {
        setTvls([]);
      });
  }, []);
  return tvls;
};

export const useV3VolumesPools = () => {
  const [volumes, setVolumes] = useState<Record<string, string>>({});

  useEffect(() => {
    getAllV3Pool24Volume()
      .then((poolVolumes) => {
        const result = {};
        poolVolumes.forEach((poolVolume) => {
          const { pool_id, volume } = poolVolume;
          result[pool_id] = volume;
        });
        setVolumes(result);
      })
      .catch(() => {
        return {};
      });
  }, []);

  return volumes;
};

export const useClassicPoolTransaction = ({
  pool_id,
}: {
  pool_id: string | number;
}) => {
  const [swapRecent, setSwapRecent] = useState<ClassicPoolSwapTransaction[]>(
    []
  );

  const [lqRecent, setLqRecent] = useState<
    ClassicPoolLiquidityRecentTransaction[]
  >([]);

  useEffect(() => {
    getClassicPoolSwapRecentTransaction({
      pool_id,
    }).then(setSwapRecent);

    getClassicPoolLiquidityRecentTransaction({
      pool_id,
    }).then(setLqRecent);
  }, []);

  return { swapTransaction: swapRecent, liquidityTransactions: lqRecent };
};

export const useDCLPoolTransaction = ({
  pool_id,
}: {
  pool_id: string | number;
}) => {
  const [swapRecent, setSwapRecent] = useState<DCLPoolSwapTransaction[]>([]);

  const [lqRecent, setLqRecent] = useState<DCLPoolLiquidityRecentTransaction[]>(
    []
  );

  const [limitOrderRecent, setLimitOrderRecent] = useState<
    LimitOrderRecentTransaction[]
  >([]);

  useEffect(() => {
    getDCLPoolSwapRecentTransaction({
      pool_id,
    }).then(setSwapRecent);

    getDCLPoolLiquidityRecentTransaction({
      pool_id,
    }).then(setLqRecent);

    getLimitOrderRecentTransaction({
      pool_id,
    }).then(setLimitOrderRecent);
  }, []);

  return {
    swapTransactions: swapRecent,
    liquidityTransactions: lqRecent,
    limitOrderTransactions: limitOrderRecent,
  };
};

export const useDCLTopBinFee = ({
  pool,
  way,
}: {
  pool: PoolInfo;
  way?: 'value' | 'display';
}) => {
  const [topBinApr, setTopBinApr] = useState<string>('-');
  useEffect(() => {
    if (!pool) return;
    const [bin, start_point, end_point] = get_config_of_dcl_pool(pool);
    getDCLTopBinFee({
      pool_id: pool.pool_id,
      bin,
      start_point,
      end_point,
    }).then((liquidityInRange) => {
      if (
        !liquidityInRange ||
        ONLY_ZEROS.test(liquidityInRange.total_liquidity)
      )
        return;
      const apr = new Big(liquidityInRange.total_fee)
        .div(liquidityInRange.total_liquidity)
        .mul(365)
        .mul(100)
        .toFixed();
      const apr_display = formatPercentage(apr);
      if (way == 'value') {
        setTopBinApr(apr);
      } else {
        setTopBinApr(apr_display);
      }
    });
  }, [pool]);

  return topBinApr;
};

function get_config_of_dcl_pool(pool: PoolInfo) {
  const pool_id = pool.pool_id;
  const { bin, rangeGear } = get_default_config_for_chart() as IChartItemConfig;
  const custom_config: IChartConfig = get_custom_config_for_chart();
  const bin_final = custom_config[pool_id]?.bin || bin;
  const rangeGear_final = custom_config[pool_id]?.rangeGear || rangeGear;
  const [price_l, price_r] = get_price_range_by_percent(
    rangeGear_final[0],
    pool
  );
  const point_l = get_point_by_price(price_l, pool);
  const point_r = get_point_by_price(price_r, pool);
  return [bin_final, point_l, point_r];
}

function get_price_range_by_percent(
  percent: number,
  pool: PoolInfo
): [string, string] {
  const { current_point } = pool;
  const p_l_r = percent / 100;
  const price = get_price_by_point(current_point, pool);
  const price_l_temp = Big(1 - p_l_r).mul(price);
  const price_l = price_l_temp.lt(0) ? '0' : price_l_temp.toFixed();
  const price_r = Big(1 + p_l_r)
    .mul(price)
    .toFixed();

  return [price_l, price_r];
}
function get_price_by_point(point: number, pool: PoolInfo) {
  const { token_x_metadata, token_y_metadata } = pool;
  const decimalRate_point =
    Math.pow(10, token_x_metadata.decimals) /
    Math.pow(10, token_y_metadata.decimals);
  const price = getPriceByPoint(point, decimalRate_point);
  return price;
}
function get_point_by_price(price: string, pool: PoolInfo) {
  const { point_delta, token_x_metadata, token_y_metadata } = pool;
  const decimalRate_point =
    Math.pow(10, token_y_metadata.decimals) /
    Math.pow(10, token_x_metadata.decimals);
  const point = getPointByPrice(point_delta, price, decimalRate_point);
  return point;
}
export const useIndexerStatus = (dep?: any) => {
  const [indexerStatus, setIndexerStatus] = useState<boolean>();

  useEffect(() => {
    getIndexerStatus()
      .then((fetchedIndexerStatus) => {
        setIndexerStatus(!!fetchedIndexerStatus);
      })
      .catch(() => {
        setIndexerStatus(false);
      });
  }, []);

  return {
    fail: typeof indexerStatus === 'boolean' && !indexerStatus,
  };
};
